import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input,
  Button,
  Badge,
  Spinner,
} from 'prosci-ui';
import {
  guidedUseCaseService,
  type LinkedResource,
  type RelatedResourceOption,
} from '@/services/guided-use-case-service';

interface RelatedResourcePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  linked: LinkedResource[];
  onAdd: (resource: LinkedResource) => void;
}

/**
 * @param {RelatedResourcePickerProps} props - the dialog state and what is already linked
 * @returns {JSX.Element} a browser over Contentful Related Resources
 */
export function RelatedResourcePicker({
  open,
  onOpenChange,
  linked,
  onAdd,
}: RelatedResourcePickerProps) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState<RelatedResourceOption | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term), 300);
    return () => clearTimeout(timer);
  }, [term]);

  useEffect(() => {
    if (!open) {
      setTerm('');
      setDebounced('');
      setSelected(null);
    }
  }, [open]);

  const { data, isFetching } = useQuery({
    queryKey: ['guidedRelatedResources', debounced],
    queryFn: () => guidedUseCaseService.searchRelatedResources(debounced),
    enabled: open,
  });

  const alreadyLinked = useMemo(
    () => new Set(linked.map((resource) => resource.contentful_entry_id)),
    [linked]
  );

  const results = data?.data ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:!max-w-[660px]">
        <DialogHeader>
          <DialogTitle>Add related resource</DialogTitle>
        </DialogHeader>

        <Input
          autoFocus
          value={term}
          placeholder="Search related resources"
          onChange={(e) => setTerm(e.target.value)}
        />

        <div className="text-xs text-muted-foreground">
          {isFetching ? 'Searching…' : `${results.length} results`}
        </div>

        <div className="max-h-[320px] overflow-y-auto">
          {isFetching && results.length === 0 && <Spinner />}

          {!isFetching && results.length === 0 && (
            <div className="px-4 py-8 text-center">
              <div className="text-sm font-semibold">
                No Related Resource matches &ldquo;{debounced}&rdquo;
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                Kaiya can only read what is published in Contentful. Add it as a Related
                Resource with AI Content filled in, then link it here.
              </div>
            </div>
          )}

          {results.map((resource) => {
            const linkedAlready = alreadyLinked.has(resource.id);
            const usable = resource.has_ai_content && !linkedAlready;

            return (
              <button
                key={resource.id}
                type="button"
                disabled={!usable}
                onClick={() => setSelected(resource)}
                className={`flex w-full items-start gap-3 border-t px-3 py-3 text-left ${
                  selected?.id === resource.id ? 'bg-primary-50' : ''
                } ${usable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">
                    {resource.title || `Untitled entry ${resource.id}`}
                    {resource.has_ai_content ? (
                      <Badge variant="success" tint className="ml-2">AI Content</Badge>
                    ) : (
                      <Badge variant="warning" tint className="ml-2">No AI Content</Badge>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {linkedAlready
                      ? 'Already linked to this element'
                      : resource.has_ai_content
                        ? [resource.type, resource.program_code].filter(Boolean).join(' · ')
                        : 'Cannot be added — fill in AI Content in Contentful first'}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              disabled={!selected}
              onClick={() => {
                if (!selected) return;
                onAdd({ contentful_entry_id: selected.id, title: selected.title });
                onOpenChange(false);
              }}
            >
              Add resource
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
